import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Polls /notifications/me every POLL_MS when a user is authenticated and
// renders a bell icon with unread counter + a dropdown feed. Clicking an
// item marks it as read and (optionally) navigates to its deep-link.
//
// Rendered by Header for both tenant and admin users - the backend scopes
// results to the current user_id automatically.
const POLL_MS = 45000; // 45s - low enough to feel live, high enough to be nice to the server

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const authed = !!(user && typeof user === 'object');

  const load = useCallback(async () => {
    if (!authed) return;
    try {
      const res = await api.get('/notifications/me');
      setItems(res.data?.notifications || []);
      setUnread(Number(res.data?.unread) || 0);
    } catch {
      // Silent: the bell shouldn't shout at the user if the endpoint is
      // unreachable. The next poll will retry.
    }
  }, [authed]);

  useEffect(() => {
    if (!authed) {
      setItems([]);
      setUnread(0);
      return undefined;
    }
    load();
    timerRef.current = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [authed, load]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) load();
  };

  const handleItemClick = async (n) => {
    setLoading(true);
    try {
      if (!n.read_at) {
        await api.patch(`/notifications/${n.id}/read`);
        setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
        setUnread(prev => Math.max(0, prev - 1));
      }
    } catch {
      // Non-blocking
    } finally {
      setLoading(false);
      if (n.link) {
        setOpen(false);
        navigate(n.link);
      }
    }
  };

  const handleMarkAll = async () => {
    setLoading(true);
    try {
      await api.post('/notifications/read-all');
      const now = new Date().toISOString();
      setItems(prev => prev.map(x => x.read_at ? x : { ...x, read_at: now }));
      setUnread(0);
    } catch {
      // Non-blocking
    } finally {
      setLoading(false);
    }
  };

  if (!authed) return null;

  const label = t('notif.title') || 'Notifications';
  const empty = t('notif.empty') || 'No notifications yet.';
  const markAllLabel = t('notif.markAllRead') || 'Mark all as read';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="relative p-2 text-[#2B2B2B] hover:text-[#0B3D2E] transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#0B3D2E] text-white text-[10px] font-semibold flex items-center justify-center px-1"
              data-testid="notification-bell-count"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[340px] p-0 border border-[#2B2B2B]/10 shadow-lg"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2B2B2B]/10">
          <p className="text-sm font-semibold text-[#2B2B2B]">{label}</p>
          {unread > 0 && (
            <button
              onClick={handleMarkAll}
              disabled={loading}
              className="text-[11px] text-[#0B3D2E] underline hover:no-underline disabled:opacity-50"
              data-testid="notification-mark-all"
            >
              {markAllLabel}
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell className="w-6 h-6 text-[#0B3D2E]/30 mx-auto mb-2" />
              <p className="text-xs text-[#5a5a5a]">{empty}</p>
            </div>
          ) : (
            <ul className="divide-y divide-[#2B2B2B]/5">
              {items.map(n => {
                const isRead = !!n.read_at;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleItemClick(n)}
                      disabled={loading}
                      className={`w-full text-left px-4 py-3 hover:bg-[#F5F3EF]/60 transition-colors flex gap-3 ${isRead ? 'opacity-60' : ''}`}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <span
                        className={`mt-1 h-2 w-2 rounded-full shrink-0 ${isRead ? 'bg-transparent' : 'bg-[#0B3D2E]'}`}
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#2B2B2B] leading-tight">{n.title}</p>
                        {n.body && (
                          <p className="text-xs text-[#5a5a5a] mt-0.5 line-clamp-2">{n.body}</p>
                        )}
                        <p className="text-[10px] text-[#5a5a5a] mt-1">{formatRelative(n.created_at)}</p>
                      </div>
                      {isRead && <Check className="w-3.5 h-3.5 text-[#5a5a5a] shrink-0 mt-1" aria-hidden />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
